import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, AfterViewInit, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { OverlayGuardsDirective } from '@shared/directives';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TooltipModule } from 'primeng/tooltip';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { CheckboxModule } from 'primeng/checkbox';
import { Popover, PopoverModule } from 'primeng/popover';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';

// Local imports
import { FilterToolbarConfig, FilterValues, FilterFieldConfig, FilterSelectConfig } from './generic-filter-toolbar.interfaces';
import { StopOnDateSelectPropagationDirective } from '@shared/directives/generic-datepicker.directive';
import { StopOnOptionSelectPropagationDirective } from '@shared/directives/generic-select.directive';
import { FilterView, UpdateFilterViewDTO, PinFilterViewDTO } from '@shared/models/filter-view.model';
import { FilterViewApiService } from '@shared/services/filter-view-api.service';
import { AuthService } from '@shared/services/auth.service';
import { ApiCallService } from '@shared/services/api-call.service';
import { PERMISSIONS } from '@shared/constants/permission.constant';
import { GenericPopoverComponent, PopoverConfig, filterViewPopoverConfig } from '@shared/components/generic-popover';

/**
 * Generic Filter Toolbar Component
 *
 * A reusable, configurable filter toolbar that can be plugged into generic-p-table
 * or used standalone. Supports text search, date ranges, multi-selects with any/all modes,
 * and priority-based filters.
 *
 * Features:
 * - Global fields: Displayed on main toolbar, update immediately without save button
 * - Popover fields: Displayed in filter popover, require save button to apply
 * - Clear all button only affects popover fields, not global fields
 * - Date ranges support independent from/to values
 *
 * Usage:
 * ```html
 * <generic-filter-toolbar
 *   [config]="filterConfig"
 *   (filtersChanged)="onFiltersChanged($event)">
 * </generic-filter-toolbar>
 * ```
 */
@Component({
    selector: 'generic-filter-toolbar',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        InputTextModule,
        DatePickerModule,
        SelectModule,
        MultiSelectModule,
        SelectButtonModule,
        TooltipModule,
        IconFieldModule,
        InputIconModule,
        CheckboxModule,
        PopoverModule,
        ConfirmDialogModule,
        StopOnDateSelectPropagationDirective,
        StopOnOptionSelectPropagationDirective,
        OverlayGuardsDirective,
        GenericPopoverComponent
    ],
    providers: [ConfirmationService],
    templateUrl: './generic-filter-toolbar.component.html',
    styleUrls: ['./generic-filter-toolbar.component.scss']
})
export class GenericFilterToolbarComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() config!: FilterToolbarConfig;
    @Output() filtersChanged = new EventEmitter<FilterValues>();

    @ViewChild('filterPopover') filterPopover!: Popover;
    @ViewChild('filterViewPopover') filterViewPopover!: GenericPopoverComponent;

    // Services
    private filterViewApiService = inject(FilterViewApiService);
    private authService = inject(AuthService);
    private apiCallService = inject(ApiCallService);
    private confirmationService = inject(ConfirmationService);
    private cdr = inject(ChangeDetectorRef);

    // Internal state
    filterValues: Record<string, any> = {};
    draftFilterValues: Record<string, any> = {}; // Draft state for overlay filters
    textSearchSubjects: Map<string, Subject<string>> = new Map();
    selectFilterSubjects: Map<string, Subject<string>> = new Map();
    selectOptions: Map<string, any[]> = new Map();
    selectLoading: Map<string, boolean> = new Map();
    isCollapsed: boolean = false;

    // Filter view panel state
    pinnedViews: FilterView[] = [];
    unpinnedViews: FilterView[] = [];
    selectedViewId: string | null = null;
    isLoadingViews: boolean = false;
    hasSharedManagePermission: boolean = false;

    // Filter View popover config and state
    filterViewPopoverConfig: PopoverConfig = { formConfig: { fields: [] }, actions: [] };
    filterViewEditMode: boolean = false;
    selectedFilterViewForEdit: FilterView | null = null;

    // Original filter values for comparison (when a view is loaded)
    originalViewFilterValues: Record<string, any> | null = null;

    private destroy$ = new Subject<void>();

    constructor() {
        // Check permissions (doesn't depend on @Input)
        this.hasSharedManagePermission = this.authService.hasPermission(PERMISSIONS.FILTER_VIEW_SHARED_MANAGE.code);

        // Note: Cannot initialize filterViewPopoverConfig here because this.config is not available yet
        // Will be initialized in ngOnInit() after config is set
    }

    // Match mode options for any/all toggle (0 = any, 1 = all)
    matchModeOptions = [
        { label: 'Any', value: 0 },
        { label: 'All', value: 1 }
    ];

    // Helper to get global fields
    get globalFields(): FilterFieldConfig[] {
        return this.config.fields.filter(field => field.location === 'global');
    }

    // Helper to get popover fields
    get popoverFields(): FilterFieldConfig[] {
        return this.config.fields.filter(field => field.location !== 'global');
    }

    // Helper to check if there are popover fields
    get hasPopoverFields(): boolean {
        return this.popoverFields.length > 0;
    }

    // Helper to count active popover filters (based on committed filterValues)
    get activePopoverFilterCount(): number {
        return this.popoverFields.filter(field => this.isFieldActive(this.filterValues, field)).length;
    }

    // Helper to check if draft has changes compared to committed values (popover fields only)
    get hasDraftChanges(): boolean {
        return this.popoverFields.some(field => {
            const draft = this.draftFilterValues[field.key];
            const committed = this.filterValues[field.key];

            if (field.type === 'text') {
                return draft !== committed;
            } else {
                // For complex types, use JSON comparison
                return JSON.stringify(draft) !== JSON.stringify(committed);
            }
        });
    }

    ngOnInit() {
        if (!this.config) {
            throw new Error('GenericFilterToolbarComponent requires a config input');
        }

        // Initialize collapsed state
        this.isCollapsed = this.config.defaultCollapsed || false;

        // Initialize filter view popover config (now that this.config is available)
        if (this.config.showFilterViewPanel && this.config.entityType) {
            this.filterViewPopoverConfig = filterViewPopoverConfig(
                () => this.onFilterViewPopoverSuccess(),
                () => this.onFilterViewPopoverCancel(),
                this.hasSharedManagePermission,
                () => this.getFilterData(this.draftFilterValues),
                this.config.entityType,
                this.filterViewApiService
            );
        }

        // Initialize filter values and text search subjects
        this.config.fields.forEach(field => {
            this.initializeField(field);
        });

        // Load filter views if enabled
        if (this.config.showFilterViewPanel && this.config.entityType) {
            this.loadFilterViews();
        }
    }

    ngAfterViewInit() {
        // Initialize options for select fields
        this.config.fields.forEach(field => {
            if ((field.type === 'multiSelect' || field.type === 'multiSelectPriority') && field.selectConfig) {
                const config = field.selectConfig;

                if (config.staticOptions) {
                    // Static mode: use staticOptions directly
                    this.selectOptions.set(field.key, this.transformOptions(config.staticOptions, config));
                } else if (config.service && config.method) {
                    // API mode: set up debounced filter and load initial data
                    const filterSubject = new Subject<string>();
                    this.selectFilterSubjects.set(field.key, filterSubject);

                    filterSubject.pipe(
                        debounceTime(300),
                        takeUntil(this.destroy$)
                    ).subscribe(filterValue => {
                        this.loadSelectOptions(field, filterValue);
                    });

                    // Load initial data
                    setTimeout(() => this.loadSelectOptions(field, ''), 0);
                }
            }
        });
    }

    /**
     * Transform raw options to the format expected by p-multiselect
     */
    private transformOptions(items: any[], config: FilterSelectConfig): any[] {
        const optionLabel = config.optionLabel || 'label';
        const optionValue = config.optionValue || 'value';

        return items.map(item => ({
            ...item,
            label: item[optionLabel],
            value: item[optionValue]
        }));
    }

    /**
     * Load options from API for a select field
     */
    private loadSelectOptions(field: FilterFieldConfig, filterValue: string = '') {
        const config = field.selectConfig;
        if (!config || !config.service || !config.method) return;

        this.selectLoading.set(field.key, true);

        // Build query for the body
        const bodyQuery: any = { ...config.baseQuery };

        if (filterValue && config.searchFields) {
            // Add search conditions
            const searchConditions = config.searchFields.map(searchField => ({
                [searchField]: { $regex: filterValue, $options: 'i' }
            }));
            bodyQuery.$or = searchConditions;
        }

        // Build pagination request
        const request = {
            limit: 100,
            offset: 0,
            query: {},
            sort: config.sortField || 'code,asc'
        };

        // Build body for POST request
        const body = bodyQuery;

        // Get the service method and check if it exists
        const serviceMethod = config.service[config.method];
        if (!serviceMethod) {
            console.error(`Method ${config.method} not found on service`);
            this.selectLoading.set(field.key, false);
            return;
        }

        // Call the service method with proper context binding (getOptions takes req and body)
        serviceMethod.call(config.service, request, body).pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: (response: any) => {
                let items = response.data?.items || [];

                // Apply transform if provided
                if (config.transformResponse) {
                    items = config.transformResponse(items);
                }

                this.selectOptions.set(field.key, this.transformOptions(items, config));
                this.selectLoading.set(field.key, false);
            },
            error: (error: any) => {
                console.error(`Error loading options for field ${field.key}:`, error);
                this.selectLoading.set(field.key, false);
            }
        });
    }

    /**
     * Handle filter event from p-multiselect
     */
    onSelectFilter(field: FilterFieldConfig, event: any) {
        const config = field.selectConfig;
        if (!config) return;

        const filterValue = event.filter || '';

        if (config.staticOptions) {
            // Client-side filtering for static options
            if (!filterValue.trim()) {
                this.selectOptions.set(field.key, this.transformOptions(config.staticOptions, config));
            } else {
                const filtered = config.staticOptions.filter(item => {
                    const label = item[config.optionLabel || 'label'];
                    return label?.toLowerCase().includes(filterValue.toLowerCase());
                });
                this.selectOptions.set(field.key, this.transformOptions(filtered, config));
            }
        } else if (config.service && config.method) {
            // Server-side filtering for API mode
            const filterSubject = this.selectFilterSubjects.get(field.key);
            if (filterSubject) {
                filterSubject.next(filterValue);
            }
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.textSearchSubjects.forEach(subject => subject.complete());
    }

    onTextChange(fieldKey: string, value: string) {
        const subject = this.textSearchSubjects.get(fieldKey);
        if (subject) {
            subject.next(value);
        }
    }

    onDateRangeChange(field: FilterFieldConfig, type: 'from' | 'to', value: Date | null) {
        const isGlobal = field.location === 'global';
        const targetValues = isGlobal ? this.filterValues : this.draftFilterValues;

        // Update the specific date (from or to)
        targetValues[field.key][type] = value;

        // Global fields emit immediately
        this.emitIfGlobal(field);
    }

    onMultiSelectChange(field: FilterFieldConfig) {
        // Global fields emit immediately, popover fields update draft only
        this.emitIfGlobal(field);
    }

    onMatchModeChange(field: FilterFieldConfig) {
        // Global fields emit immediately, popover fields update draft only
        this.emitIfGlobal(field);
    }

    onPriorityModeChange(field: FilterFieldConfig) {
        // Global fields emit immediately, popover fields update draft only
        this.emitIfGlobal(field);
    }

    applyFilters() {
        // Commit draft values to filterValues (popover fields only)
        this.popoverFields.forEach(field => {
            if (field.type === 'text') {
                this.filterValues[field.key] = this.draftFilterValues[field.key];
            } else {
                // For complex types (dateRange, multiSelect, multiSelectPriority), shallow copy is sufficient
                this.filterValues[field.key] = { ...this.draftFilterValues[field.key] };
            }
        });
        console.log('Filters saved:', this.filterValues);

        this.emitFilters();
    }

    clearFilters() {
        // Reset popover filter values only (both committed and draft)
        // Global filters are NOT affected by clear
        this.popoverFields.forEach(field => {
            this.resetField(field);
        });

        this.emitFilters();
    }

    toggleCollapse() {
        this.isCollapsed = !this.isCollapsed;
    }

    private emitFilters() {
        // Build the filter object to emit (matching SearchEnrolmentDTO structure)
        const filters: FilterValues = {};

        this.config.fields.forEach(field => {
            const value = this.filterValues[field.key];
            const filterValue = this.buildFilterValue(field, value);

            if (filterValue !== null) {
                filters[field.key] = filterValue;
            }
        });

        this.filtersChanged.emit(filters);
        console.log('Filters emitted:', filters);
    }

    /**
     * Build the filter value to emit based on field type and value
     * Returns null if the value should not be included in the filter
     */
    private buildFilterValue(field: FilterFieldConfig, value: any): any {
        if (field.type === 'text') {
            return value && value.trim() ? value.trim() : null;
        } else if (field.type === 'dateRange') {
            // Emit as RangeDateDTO with from and/or to independently
            const rangeFilter: any = {};
            if (value.from) rangeFilter.from = value.from;
            if (value.to) rangeFilter.to = value.to;

            // Only return if at least one date is present
            return (rangeFilter.from || rangeFilter.to) ? rangeFilter : null;

        } else if (field.type === 'multiSelect') {
            // Emit based on whether field is array or single-value
            if (value.values && value.values.length > 0) {
                if (field.isArrayField) {
                    // Array field: emit { values: string[], mode: 0|1 }
                    return {
                        values: value.values,
                        mode: value.mode
                    };
                } else {
                    // Single-value field: emit array only (mode is always 'any')
                    return value.values;
                }
            }
            return null;
        } else if (field.type === 'multiSelectPriority') {
            // Emit as SearchPriorityDTO { values: string[], mode: 0|1, priority: -1|0|1 }
            if (value.values && value.values.length > 0) {
                return {
                    values: value.values,
                    mode: value.mode,
                    priority: value.priority
                };
            }
            return null;
        }
        return null;
    }

    // Helper method to get field by key
    getField(key: string): FilterFieldConfig | undefined {
        return this.config.fields.find(f => f.key === key);
    }

    // Helper to check if any filters are active
    get hasActiveFilters(): boolean {
        return this.config.fields.some(field => this.isFieldActive(this.filterValues, field));
    }

    // Helper to convert priority mode options from string to numeric values
    getPriorityModeOptionsWithNumericValues(options: Array<{ label: string; value: string }>): Array<{ label: string; value: number }> {
        return options.map(opt => ({
            label: opt.label,
            value: this.getPriorityNumericValue(opt.value)
        }));
    }

    private cloneFilterData<T>(value: T): T {
        return value === undefined ? value : JSON.parse(JSON.stringify(value));
    }

    /**
     * Clone filter data and preserve Date objects for dateRange fields
     * This helper clones the data and then converts date strings back to Date objects
     */
    private cloneAndPreserveDates(filterData: any): any {
        const cloned = this.cloneFilterData(filterData);

        // Convert date strings back to Date objects for dateRange fields
        this.config.fields.forEach(field => {
            if (field.type === 'dateRange' && cloned[field.key]) {
                const value = cloned[field.key];
                if (value.from && typeof value.from === 'string') {
                    value.from = new Date(value.from);
                }
                if (value.to && typeof value.to === 'string') {
                    value.to = new Date(value.to);
                }
            }
        });

        return cloned;
    }

    /**
     * Convert priority string value to numeric value
     */
    private getPriorityNumericValue(value: string): number {
        return value === 'positive' ? 1 : value === 'zero' ? 0 : -1;
    }

    /**
     * Get default priority value for a field
     */
    private getDefaultPriorityValue(field: FilterFieldConfig): number {
        const defaultPriority = field.priorityModeOptions?.[0]?.value || 'undefined';
        return this.getPriorityNumericValue(defaultPriority);
    }

    /**
     * Create default value for a field based on its type
     */
    private createDefaultFieldValue(field: FilterFieldConfig): any {
        if (field.type === 'text') return '';
        if (field.type === 'dateRange') return { from: null, to: null };
        if (field.type === 'multiSelect') return { values: [], mode: 0 };
        if (field.type === 'multiSelectPriority') return { values: [], mode: 0, priority: this.getDefaultPriorityValue(field) };
        return null;
    }

    /**
     * Reset a field to its default value
     * @param field The field to reset
     * @param targets The target objects to reset (defaults to both filterValues and draftFilterValues)
     */
    private resetField(field: FilterFieldConfig, targets: Array<Record<string, any>> = [this.filterValues, this.draftFilterValues]): void {
        const defaultValue = this.createDefaultFieldValue(field);
        targets.forEach(target => (target[field.key] = this.cloneFilterData(defaultValue)));
    }

    /**
     * Initialize a single field with default values and set up text search subject if needed
     */
    private initializeField(field: FilterFieldConfig): void {
        const isGlobal = field.location === 'global';

        // Initialize default values for both committed and draft state
        this.resetField(field);

        // Set up text search subject for text fields
        if (field.type === 'text') {
            const subject = new Subject<string>();
            const debounce = field.debounceTime || 300;

            // Global fields emit immediately
            if (isGlobal) {
                subject.pipe(
                    debounceTime(debounce),
                    takeUntil(this.destroy$)
                ).subscribe(value => {
                    this.filterValues[field.key] = value;
                    this.emitFilters();
                });
            } else {
                // Popover fields update draft only
                subject.pipe(
                    debounceTime(debounce),
                    takeUntil(this.destroy$)
                ).subscribe(value => {
                    this.draftFilterValues[field.key] = value;
                });
            }

            this.textSearchSubjects.set(field.key, subject);
        }
    }

    /**
     * Check if a field has an active filter value
     * @param values The values object to check
     * @param field The field configuration
     */
    private isFieldActive(values: Record<string, any>, field: FilterFieldConfig): boolean {
        const value = values[field.key];
        if (field.type === 'text') return !!value?.trim();
        if (field.type === 'dateRange') return !!(value?.from || value?.to);
        if (field.type === 'multiSelect' || field.type === 'multiSelectPriority') return (value?.values?.length || 0) > 0;
        return false;
    }

    private emitIfGlobal(field: FilterFieldConfig): void {
        if (field.location === 'global') this.emitFilters();
    }

    // ========== Filter View Panel Methods ==========

    loadFilterViews(): void {
        if (!this.config.entityType) return;

        this.isLoadingViews = true;
        this.apiCallService.fetch({
            serviceCall$: this.filterViewApiService.getFilterViews(this.config.entityType),
            onSuccess: (data: any) => {
                // Backend returns { total: -1, items: FilterView[] }
                const allViews: FilterView[] = (data.items || []).sort((a: any, b: any) => {
                    return a.isShared === b.isShared ? 0 : a.isShared ? -1 : 1;
                });

                // Separate views by pinned status
                this.pinnedViews = allViews.filter(v => v.isPinned);
                this.unpinnedViews = allViews.filter(v => !v.isPinned);
            },
            finalize: () => {
                this.isLoadingViews = false;
            }
        });
    }

    selectFilterView(view: FilterView): void {
        // If clicking the same view, unselect it
        if (this.selectedViewId === view.id) {
            this.unselectFilterView();
            return;
        }

        // Batch all updates to avoid multiple change detection cycles
        this.cdr.detach();

        this.selectedViewId = view.id;
        this.loadFilterViewData(view.filterData);
        this.originalViewFilterValues = this.cloneAndPreserveDates(view.filterData);

        this.cdr.reattach();
        this.cdr.detectChanges();
    }

    unselectFilterView(): void {
        this.selectedViewId = null;
        this.originalViewFilterValues = null;
        this.clearAllFilters();
    }

    loadFilterViewData(filterData: any): void {
        // Clone and preserve dates for both filterValues and draftFilterValues
        this.filterValues = this.cloneAndPreserveDates(filterData);
        this.draftFilterValues = this.cloneAndPreserveDates(filterData);

        // Reset fields that are not in filterData to their defaults
        this.config.fields.forEach(field => {
            if (filterData[field.key] === undefined || filterData[field.key] === null) {
                this.resetField(field);
            }
        });

        this.emitFilters();
    }



    clearAllFilters(): void {
        // Clear all filters (both global and popover)
        this.config.fields.forEach(field => {
            this.resetField(field);
        });
        this.emitFilters();
    }

    async saveCurrentFilterView(): Promise<void> {
        if (!this.selectedViewId) return;

        const selectedView = this.getSelectedView();
        if (!selectedView) return;

        const updateData: UpdateFilterViewDTO = {
            id: this.selectedViewId,
            filterData: this.getFilterData(this.draftFilterValues)
        };

        const result = await this.apiCallService.execute(
            this.filterViewApiService.updateFilterView(updateData),
            'Đã lưu bộ lọc'
        );

        if (result.success) {
            this.originalViewFilterValues = this.cloneAndPreserveDates(this.getFilterData(this.draftFilterValues));
            this.loadFilterViews();
        }
    }

    openSaveAsPopover(event: Event): void {
        this.filterViewEditMode = false;
        this.selectedFilterViewForEdit = null;
        this.filterViewPopover.show(event);
    }

    openEditFilterViewPopover(view: FilterView, event: Event): void {
        this.filterViewEditMode = true;
        this.selectedFilterViewForEdit = view;
        this.filterViewPopover.show(event);
    }

    onFilterViewPopoverSuccess(): void {
        this.filterViewPopover.hide();
        this.selectedFilterViewForEdit = null;
        this.loadFilterViews();

        // If we just created a new view, we could select it here
        // But we don't have access to the created ID in this callback
        // The config handles the API call, so we just reload the list
    }

    onFilterViewPopoverCancel(): void {
        this.filterViewPopover.hide();
        this.selectedFilterViewForEdit = null;
    }

    async pinFilterView(view: FilterView): Promise<void> {
        const pinData: PinFilterViewDTO = {
            id: view.id,
            isPinned: !view.isPinned
        };

        const result = await this.apiCallService.execute(
            this.filterViewApiService.pinFilterView(pinData),
            view.isPinned ? 'Đã bỏ ghim' : 'Đã ghim'
        );

        if (result.success) {
            this.loadFilterViews();
        }
    }

    deleteFilterView(view: FilterView): void {
        this.confirmationService.confirm({
            message: `Bạn có chắc chắn muốn xóa bộ lọc "${view.name}"?`,
            header: 'Xác nhận xóa',
            icon: 'pi pi-exclamation-triangle',
            closeOnEscape: true,
            dismissableMask: true,
            acceptLabel: 'Xóa',
            rejectLabel: 'Hủy',
            accept: async () => {
                const result = await this.apiCallService.execute(
                    this.filterViewApiService.deleteFilterView(view.id),
                    'Đã xóa bộ lọc'
                );

                if (result.success) {
                    if (this.selectedViewId === view.id) {
                        this.unselectFilterView();
                    }
                    this.loadFilterViews();
                }
            }
        });
    }

    /**
     * Extract filter data from a values object (either filterValues or draftFilterValues)
     * Only includes non-global fields with active values
     */
    private getFilterData(values: Record<string, any>): any {
        const filterData: any = {};
        // Only include non-global fields in saved filter data
        this.config.fields.forEach(field => {
            // Skip global fields - they should not be saved
            if (field.location === 'global') return;

            const value = values[field.key];
            if (this.isFieldActive(values, field)) {
                filterData[field.key] = value;
            }
        });
        return filterData;
    }

    getSelectedView(): FilterView | null {
        if (!this.selectedViewId) return null;
        return [...this.pinnedViews, ...this.unpinnedViews].find(v => v.id === this.selectedViewId) || null;
    }

    get canSaveCurrentView(): boolean {
        // console.log('canSaveCurrentView fired');
        // console.log('canSaveCurrentView', this.selectedViewId, this.getSelectedView(), this.hasFilterChangesFromOriginal());
        if (!this.selectedViewId) return false;
        const selectedView = this.getSelectedView();
        if (!selectedView) return false;

        // Check if user can edit this view
        if (selectedView.isShared && !this.hasSharedManagePermission) return false;

        // Check if there are changes
        return this.hasFilterChangesFromOriginal();
    }

    get saveButtonTooltip(): string {
        if (!this.selectedViewId) return '';
        const selectedView = this.getSelectedView();
        if (!selectedView) return '';

        if (selectedView.isShared && !this.hasSharedManagePermission) return 'Bạn không có quyền chỉnh sửa bộ lọc chia sẻ';

        if (!this.hasFilterChangesFromOriginal()) return 'Không có thay đổi';

        return '';
    }

    hasFilterChangesFromOriginal(): boolean {
        if (!this.originalViewFilterValues) return false;
        const draft = this.getFilterData(this.draftFilterValues);
        return JSON.stringify(draft) !== JSON.stringify(this.originalViewFilterValues);
    }
}

